import { z } from 'zod';

// A calendar day in the venue's own timezone; the server decides which is today.
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');

export type IsoDate = string;

export const DAY_MODES = ['past', 'live', 'future'] as const;

export const dayModeSchema = z.enum(DAY_MODES);

export type DayMode = z.infer<typeof dayModeSchema>;

// ISO days compare as strings, so no clock and no timezone enters the comparison.
export function dayModeOf(date: IsoDate, today: IsoDate): DayMode {
  if (date === today) return 'live';
  return date < today ? 'past' : 'future';
}

export function shiftDay(date: IsoDate, days: number): IsoDate {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const moved = new Date(Date.UTC(year, month - 1, day + days));
  return formatIsoDate(moved);
}

// UTC parts only: a local getter would shift the day west of Greenwich.
export function formatIsoDate(at: Date): IsoDate {
  const month = String(at.getUTCMonth() + 1).padStart(2, '0');
  const day = String(at.getUTCDate()).padStart(2, '0');
  return `${at.getUTCFullYear()}-${month}-${day}`;
}

// The calendar travels with the selected day: context behind, plans ahead. §15
export const CALENDAR_BACK = 3;
export const CALENDAR_FORWARD = 10;

export function calendarWindow(
  selected: IsoDate,
  today: IsoDate,
  back = CALENDAR_BACK,
  forward = CALENDAR_FORWARD,
): StripDay[] {
  const days: StripDay[] = [];
  for (let offset = -back; offset <= forward; offset += 1) {
    const date = shiftDay(selected, offset);
    days.push({ date, mode: dayModeOf(date, today), offset });
  }
  return days;
}

export type StripDay = { readonly date: IsoDate; readonly mode: DayMode; readonly offset: number };

// The strip is centred on the selected day; phones ask for a smaller span. §8
export function dayStrip(selected: IsoDate, today: IsoDate, span = 3): StripDay[] {
  const days: StripDay[] = [];
  for (let offset = -span; offset <= span; offset += 1) {
    const date = shiftDay(selected, offset);
    days.push({ date, mode: dayModeOf(date, today), offset });
  }
  return days;
}
